import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Box, Button, Card, Flex, Spinner, Text } from "@radix-ui/themes";
import { isEmpty } from "lodash";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetAcademicYearQuery } from "../../hooks/useGetAcademicYearQuery";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { resetUpdateCoursePlanData } from "../../redux/slices/school/coursePlan";
import { RootState } from "../../redux/store";
import { routes } from '../../routes';
import { createCoursePlan, updateCoursePlan } from "../../services/school/CoursePlans";
import { CoursePlanType } from "../../types/CoursePlan";
import { dataURLtoFile, getFile, getFileExt } from "../../utils/fileUtils";
import { CreateUpdateCoursePlanFormValidation } from "./CreateUpdateCoursePlanFormValidation";
import { useGetUniversitiesQuery } from "../../hooks/useGetUniversitiesQuery";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";

export const CreateUpdateCoursePlan = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const isUpdateRoute = location.pathname === routes.updateCoursePlan;

    const dispatch = useAppDispatch();
    const updateCoursePlanFormData = useSelector((state: RootState) => state.coursePlan);
    let defaultValues: CoursePlanType = { ...updateCoursePlanFormData };
    if (!isEmpty(updateCoursePlanFormData.docBase64)) {
        const file = dataURLtoFile(updateCoursePlanFormData.docBase64, getFileExt(updateCoursePlanFormData.docBase64, 'plan.pdf'));
        defaultValues = { ...updateCoursePlanFormData, docBase64: file };
    }

    const mutation = useMutation({ mutationFn: ((defaultValues.id && isUpdateRoute) ? updateCoursePlan : createCoursePlan), mutationKey: 'createUpdateCoursePlan' });
    const [currentPage, setCurrentPage] = useState(1);

    const { control, handleSubmit, setValue, getValues, formState: { errors }, resetField, watch } = useForm<CoursePlanType>({
        resolver: yupResolver(CreateUpdateCoursePlanFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    })
    const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('idUniversity') });
    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
        { PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
        { enabled: !!getValues('idFaculty') }
    );
    const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery(
        { PageNumber: 1, PageSize: 400, IdDepartament: getValues('idDepartament') },
        { enabled: !!getValues('idDepartament') }
    );

    const { data: academicYears, isLoading: isLoadingAcademicYears, isError: errorAcademicYears } = useGetAcademicYearQuery({ PageNumber: currentPage, PageSize: 400 });

    const onSubmit = handleSubmit(async (data) => {

        try {
            if (data?.docBase64) {
                const { base64 } = await getFile(data.docBase64);
                data.docBase64 = base64;
                data.extension = getFileExt(data?.docBase64, 'plan.pdf');
            } else {
                data.docBase64 = '';
            }

            const result = await mutation.mutateAsync(data);

            if (result) {
                toast.success(`Plano do curso ${(defaultValues.id && isUpdateRoute) ? 'atualizado' : 'registado'}.`);
                handleBack();
            }

        } catch (error: any) {
            toast.error(`${mutation.error ?? error?.message}`);
        }
    });

    const handleFacultyChange = (idFaculty: string) => {
        setValue('idFaculty', idFaculty);
        refreshDepartaments();
    }

    const handleDepartamentChange = (idDepartament: string) => {
        setValue('idDepartament', idDepartament);
        refreshCourses();
    }

    const handleCourseChange = async (idCourse: string) => {
        setValue('idCourse', idCourse);
    }

    const handleBack = () => {
        dispatch(resetUpdateCoursePlanData());
        navigate(routes.coursePlans);
    }
    const handleUniversityChange = (id: string) => {
        setValue('idUniversity', id);
        setValue('idFaculty', '');
        setValue('idDepartament', '');
        refetchFaculties();
    };
    return (
        <form onSubmit={onSubmit} noValidate>
            <Card size='1'>
                <Flex direction='column' gap='4'>
                    <SelectComponent
                        name="idUniversity"
                        label="Instituição de Ensino Superior"
                        control={control}
                        errors={errors}
                        style={{ width: '100%' }}
                        options={universities?.items.map(u => ({
                            value: u.id ?? "",
                            label: u.description ?? ""
                        }))}
                        itemsLoading={isLoadingUniversities}
                        itemsError={errorUniversities}
                        onSelect={handleUniversityChange}
                        required={true}
                    />
                    <SelectComponent name='idFaculty' label='Unidade orgânica' control={control} errors={errors} style={{ width: '100%' }}
                        options={faculties?.items.map(faculty => ({
                            value: faculty.id ?? '',
                            label: faculty.description ?? ''
                        }))}
                        onSelect={handleFacultyChange}
                        required={true}
                    />
                    <SelectComponent name='idDepartament' label='Departamento' control={control} errors={errors} style={{ width: '100%' }}
                        options={departaments?.items.map(departament => ({
                            value: departament.id ?? '',
                            label: departament.description ?? ''
                        }))}
                        onSelect={handleDepartamentChange}
                        required={true}
                    />
                    <SelectComponent name='idCourse' label='Curso' control={control} errors={errors} style={{ width: '100%' }}
                        options={courses?.items.map(course => ({
                            value: course.id ?? '',
                            label: course.name ?? ''
                        }))}
                        onSelect={handleCourseChange}
                        required={true}
                    />
                    <SelectComponent name='idAcademicYear' label='Ano académico' control={control} errors={errors} style={{ width: '100%' }}
                        options={academicYears?.items.map(ay => ({
                            value: ay.id ?? '',
                            label: ay.year ?? ''
                        }))}
                        itemsError={errorAcademicYears}
                        itemsLoading={isLoadingAcademicYears}
                        required={true}
                    />
                    <TextAreaFieldComponent name="skills" label='Competências' control={control} errors={errors} fieldProps={{ placeholder: 'Competências' }} style={{ width: '100%' }} required={false} />
                    <Box>
                        <Text as="label" size="2"><span>Plano curricular <Text color="red" aria-hidden="true">*</Text></span></Text>
                        <Controller
                            name={`docBase64`}
                            control={control}
                            render={({ field }) => (
                                <DragAndDropFileUpload
                                    onFileDrop={(file) => {
                                        field.onChange(file);
                                    }}
                                    label={`Arraste ou seleccione o ficheiro`}
                                    accept='*'
                                    selectedFile={field.value}
                                />
                            )}
                        />
                        {errors.docBase64 && (
                            <Text color='red' size='1'>{`${errors.docBase64.message}`}</Text>
                        )}
                    </Box>

                    <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                        <Button type='button' variant='soft' color='gray' onClick={handleBack} disabled={mutation.isLoading}>
                            <ChevronLeftIcon /> Voltar
                        </Button>
                        <Button type='submit' disabled={mutation.isLoading} variant='surface'>
                            {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                            {(defaultValues.id && isUpdateRoute) ? 'Atualizar' : 'Registar'}
                        </Button>
                    </Flex>
                </Flex>
            </Card>
        </form>
    );
}